import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BillingService,
  billingStrategyFromAPI,
  CanCreateSubscriptionsResponse,
  GetPurchaseCostResponse,
  isBillingStrategyInstant,
  SubscribeValidationStatus,
  ValidateResponse,
} from '@galaxy/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { combineLatest, EMPTY, Observable, ReplaySubject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { BillingSettingsService } from '../shared-billing/settings/billing-settings.service';

export enum BillingAction {
  PAYMENT_CONFIGURED = 1,
  NO_PAYMENT_CONFIGURED,
  INVALID_PAYMENT,
  NO_INSTANT_PAYMENT_NEEDED,
  NO_MERCHANT_CONFIGURED,
}

export interface PaymentInfo {
  lastFourDigits: string;
  paymentClass: string;
}

interface CanCreateSubscriptionsRequest {
  merchantId: string;
  productSKU: string;
  quantity: number;
}

@Injectable()
export class CheckoutService {
  canCreateSubscriptionsRequest$$: ReplaySubject<CanCreateSubscriptionsRequest> =
    new ReplaySubject<CanCreateSubscriptionsRequest>();
  billingAction$: Observable<BillingAction>;

  constructor(
    private billingSettingsService: BillingSettingsService,
    private billingService: BillingService,
    private snackbarService: SnackbarService,
  ) {
    this.initBillingStatus();
  }

  initBillingStatus(): void {
    const validationResponse$ = this.canCreateSubscriptionsRequest$$.asObservable().pipe(
      tap(({ merchantId }) => {
        this.billingSettingsService.merchantId = merchantId;
      }),
      switchMap(({ merchantId, productSKU, quantity }) => {
        return this.getSubscriptionValidation(merchantId, productSKU, quantity);
      }),
    );
    const purchaseCost$ = this.canCreateSubscriptionsRequest$$.asObservable().pipe(
      switchMap(({ merchantId, productSKU, quantity }) => {
        return this.billingService.getPurchaseCost(merchantId, productSKU, quantity);
      }),
      catchError(() => {
        this.snackbarService.errorSnack('Unable to get Purchase Cost');
        return null;
      }),
    );
    this.billingAction$ = combineLatest([purchaseCost$, validationResponse$]).pipe(
      map(([cost, validation]) => this.getBillingAction(cost as GetPurchaseCostResponse, validation)),
      tap((billingAction) => {
        if (billingAction === BillingAction.PAYMENT_CONFIGURED) {
          this.billingSettingsService.loadMerchantCreditCards();
        }
      }),
    );
  }

  getBillingAction(purchaseCost: GetPurchaseCostResponse, validationResponse: ValidateResponse): BillingAction {
    if (validationResponse) {
      switch (validationResponse.code.status) {
        case SubscribeValidationStatus.SUBSCRIBE_VALIDATION_STATUS_MISSING_MERCHANT_INFO:
          return BillingAction.NO_MERCHANT_CONFIGURED;
        case SubscribeValidationStatus.SUBSCRIBE_VALIDATION_STATUS_MISSING_PAYMENT_INFO:
          return BillingAction.NO_PAYMENT_CONFIGURED;
        case SubscribeValidationStatus.SUBSCRIBE_VALIDATION_STATUS_CANNOT_INSTANT_BILL:
          throw new Error('Cannot instant bill for this product');
        case SubscribeValidationStatus.SUBSCRIBE_VALIDATION_STATUS_WILL_CAUSE_FUTURE_CHARGES:
          // checkout service currently only deals with snapshots, snapshots aren't a recurring product we won't see this case for snapshots.
          // This is for if we decided to use this service for other products, we would need to handle this case.
          throw new Error('Not Implemented: handling will exceed free usage');
      }
    }
    if (isBillingStrategyInstant(billingStrategyFromAPI(purchaseCost.strategy)) && purchaseCost.totalCost > 0) {
      return BillingAction.PAYMENT_CONFIGURED;
    }
    return BillingAction.NO_INSTANT_PAYMENT_NEEDED;
  }

  getSubscriptionValidation(merchantId: string, productSKU: string, quantity: number): Observable<ValidateResponse> {
    return this.canCreateSubscriptions(merchantId, {
      [productSKU]: quantity,
    }).pipe(
      map((validationViolations) => validationViolations?.find((violation) => violation.sku === productSKU) || null),
    );
  }

  loadProductRequest(merchantId: string, productSKU: string, quantity = 1): void {
    this.canCreateSubscriptionsRequest$$.next({
      merchantId: merchantId,
      productSKU: productSKU,
      quantity: quantity,
    });
  }

  canCreateSubscriptions(merchantId: string, skus: { [sku: string]: number }): Observable<ValidateResponse[]> {
    return this.billingService.canCreateSubscriptions(merchantId, skus).pipe(
      map((response: CanCreateSubscriptionsResponse) => (response ? response.validationResponse || [] : null)),
      catchError((error: HttpErrorResponse): Observable<ValidateResponse[]> => {
        if (error.status >= 500) {
          this.snackbarService.errorSnack('Uh-oh. Looks like something went wrong on our end. Please try again.');
        } else if (error.status >= 400) {
          this.snackbarService.errorSnack(error.message);
        }
        return EMPTY;
      }),
    );
  }
}
